import React from "react";
import { Link } from "react-router-dom";
import SubText from "components/shared/SubText";
import TextSkeleton from "components/skeleton/videoDetailsSkeleton/TextSkeleton";
import { RouteName } from "constant/routeNames";
import { decodeHtmlEntities } from "constant/htmlEntities";

type Person = {
  name?: string;
  slug?: string;
};

/** API may return tags as a comma-separated string or an array. */
type Tags = string | Array<string | { name?: string }>;

type Video = {
  description?: string;
  country?: string;
  tags?: Tags;
  directors?: Person[];
  producers?: Person[];
  actors?: Person[];
};

type DetailsProps = {
  video?: Video;
  isLoading?: boolean;
};

/** Decode the HTML entities the API returns in plain-text fields (all of
 *  them - not just the handful this used to hardcode, which is why things
 *  like &pound; were showing up as literal text). */
const sanitize = (value?: string): string => decodeHtmlEntities(value).trim();

const creatorPath = (slug?: string): string =>
  RouteName.VideoCreatorHomeRoute.replace(":slug", `@${slug ?? ""}`);

/** Normalise tags (string or array) into a clean list of labels. */
const parseTags = (tags?: Tags): string[] => {
  if (!tags) return [];
  const raw = Array.isArray(tags)
    ? tags.map((t) => (typeof t === "string" ? t : t?.name ?? ""))
    : tags.split(",");
  return raw.map((t) => sanitize(t)).filter(Boolean);
};

/** A labelled row of linked people (directors, producers, actors). */
function CreditRow({ label, people }: { label: string; people?: Person[] }) {
  if (!people?.length) return null;

  return (
    <section className="mt-8 first:mt-0">
      <SubText text={label} />
      <p className="mt-3 text-base font-normal leading-relaxed text-white">
        {people.map((person, i) => (
          <React.Fragment key={person.slug ?? `${person.name}-${i}`}>
            <Link
              to={creatorPath(person.slug)}
              className="transition-colors hover:text-white/80 hover:underline"
            >
              {sanitize(person.name)}
            </Link>
            {i !== people.length - 1 && (
              <span className="text-white/50">, </span>
            )}
          </React.Fragment>
        ))}
      </p>
    </section>
  );
}

/** A labelled row of plain text (e.g. Country). */
function InfoRow({ label, value }: { label: string; value?: string }) {
  const text = sanitize(value);
  if (!text) return null;

  return (
    <section className="mt-8 first:mt-0">
      <p className="text-lg font-medium text-white">{label}</p>
      <p className="mt-3 text-base font-normal text-white">{text}</p>
    </section>
  );
}

/** A labelled row of tag chips. */
function TagRow({ label, tags }: { label: string; tags: string[] }) {
  if (!tags.length) return null;

  return (
    <section className="mt-8 first:mt-0">
      <SubText text={label} />
      <div className="mt-3 flex flex-wrap gap-2">
        {tags.slice(0, 10).map((tag, i) => (
          <span
            key={`${tag}-${i}`}
            className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-sm font-normal text-white/90 transition-colors hover:bg-white/10"
          >
            {tag}
          </span>
        ))}
      </div>
    </section>
  );
}

function Details({ video, isLoading }: DetailsProps) {
  if (isLoading) return <TextSkeleton />;
  if (!video) return null;

  const country = sanitize(video.country);
  const description = sanitize(video.description);
  const tags = parseTags(video.tags);
  const hasDescription = !!description;
  const hasInfo = !!country || tags.length > 0;
  const hasCredits = !!(
    video.directors?.length ||
    video.producers?.length ||
    video.actors?.length
  );

  if (!hasDescription && !hasInfo && !hasCredits) return null;

  return (
    <div className="mt-10 flex w-full flex-col divide-y divide-white/10">
      {hasDescription && (
        <div className="py-8 first:pt-0 last:pb-0">
          <SubText text="More Info" />
          <p className="mt-3 whitespace-pre-line text-base font-normal leading-relaxed text-white/90">
            {description}
          </p>
        </div>
      )}

      {hasInfo && (
        <div className="py-8 first:pt-0 last:pb-0">
          {/* <SubText text="Tags" /> */}
          <TagRow label="" tags={tags} />
        </div>
      )}

      {hasCredits && (
        <div className="py-8 first:pt-0 last:pb-0">
          <CreditRow label="Directors" people={video.directors} />
          <CreditRow label="Producers" people={video.producers} />
          <CreditRow label="Starring" people={video.actors} />
        </div>
      )}
    </div>
  );
}

export default Details;